import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import {
  SocialPlatform,
  SocialSentiment,
  SocialTopicAuthorRef,
  SocialTopicPostItem,
} from "@digital-twins/shared-types";

/**
 * What the page is pointing at: a hover or focus on a number, a band, a row,
 * a day of the chart or a post. Matching posts stay lit, the others dim.
 * `day` is a UTC day, `YYYY-MM-DD`.
 */
export type SocialTopicHighlight =
  | { kind: "post"; postToken: string }
  | { kind: "author"; author: SocialTopicAuthorRef }
  | { kind: "brand"; brandId: string }
  | { kind: "channel"; platform: SocialPlatform }
  | { kind: "sentiment"; sentiment: SocialSentiment }
  | { kind: "day"; day: string };

export type SocialTopicHighlightKind = SocialTopicHighlight["kind"];

export interface SocialTopicHighlightValue {
  active: SocialTopicHighlight | null;
  setActive: (highlight: SocialTopicHighlight | null) => void;
  /**
   * True when a highlight is active and `post` does not match it. A post
   * highlight never dims the other posts (hovering a tile keeps its neighbors).
   */
  isDimmed: (post: SocialTopicPostItem) => boolean;
}

const NO_HIGHLIGHT: SocialTopicHighlightValue = {
  active: null,
  setActive: () => {},
  isDimmed: () => false,
};

const SocialTopicHighlightContext = createContext<SocialTopicHighlightValue>(NO_HIGHLIGHT);

/** Like the server's handle normalization: no edge spaces, no leading `@`, lower case. */
function normalizeHandle(handle: string): string {
  return handle.trim().replace(/^@+/, "").toLowerCase();
}

/** The post's UTC publish day, `YYYY-MM-DD`; null when it has no valid date. */
function publishDay(post: Pick<SocialTopicPostItem, "publishedAt">): string | null {
  if (!post.publishedAt) {
    return null;
  }
  const time = Date.parse(post.publishedAt);
  return Number.isNaN(time) ? null : new Date(time).toISOString().slice(0, 10);
}

/** Pure: whether `post` matches `highlight` (a post highlight matches every post). */
export function postMatchesHighlight(
  post: SocialTopicPostItem,
  highlight: SocialTopicHighlight,
): boolean {
  switch (highlight.kind) {
    case "post":
      return true;
    case "author":
      return (
        post.platform === highlight.author.platform &&
        post.authorHandle != null &&
        normalizeHandle(post.authorHandle) === normalizeHandle(highlight.author.handle)
      );
    case "brand":
      return post.brands.some((brand) => brand.id === highlight.brandId);
    case "channel":
      return post.platform === highlight.platform;
    case "sentiment":
      return post.sentiment === highlight.sentiment;
    case "day":
      return publishDay(post) === highlight.day;
  }
}

interface SocialTopicHighlightProviderProps {
  children: ReactNode;
}

/** Holds the page's one highlight; every board, chart and panel reads it. */
export function SocialTopicHighlightProvider(props: SocialTopicHighlightProviderProps) {
  const { children } = props;

  const [active, setActiveState] = useState<SocialTopicHighlight | null>(null);

  const setActive = useCallback((highlight: SocialTopicHighlight | null) => {
    setActiveState(highlight);
  }, []);

  const isDimmed = useCallback(
    (post: SocialTopicPostItem) => active != null && !postMatchesHighlight(post, active),
    [active],
  );

  const value = useMemo(() => ({ active, setActive, isDimmed }), [active, setActive, isDimmed]);

  return (
    <SocialTopicHighlightContext.Provider value={value}>
      {children}
    </SocialTopicHighlightContext.Provider>
  );
}

/** The page's highlight; outside a provider (the tracker drawer) nothing is ever dimmed. */
export function useTopicHighlight(): SocialTopicHighlightValue {
  return useContext(SocialTopicHighlightContext);
}
